import * as React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@shopmuchmore/design-system';
import { Button, Paragraph } from '@shopmuchmore/design-system';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@shopmuchmore/design-system';
import { useConfirmCancelOrderMutation } from '../checkout.services';
import { useAuthStore } from '../../auth/auth.store';
import { Loader2 } from 'lucide-react';

interface OrderCancellationModalProps {
    open: boolean;
    orderId: string;
    onClose: () => void;
    onConfirm: () => void;
}

export function OrderCancellationModal({ open, orderId, onClose, onConfirm }: OrderCancellationModalProps) {
    const [otp, setOtp] = React.useState('');
    const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
    const [isSuccess, setIsSuccess] = React.useState(false);

    const { customer } = useAuthStore();
    const { mutate: confirmCancel, isPending } = useConfirmCancelOrderMutation();

    const handleConfirm = () => {
        if (otp.length !== 6) return;
        
        setErrorMsg(null);
        confirmCancel(
            {
                order_id: orderId,
                verification_token: otp,
                email: customer?.email || '',
            },
            {
                onSuccess: () => {
                    setIsSuccess(true);
                    setTimeout(() => {
                        onConfirm();
                        onClose();
                    }, 2000);
                },
                onError: (err: any) => {
                    setErrorMsg(err?.response?.data?.message || 'Invalid or expired token.');
                }
            }
        );
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[500px] rounded-[32px] p-8">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-black text-gray-900 leading-tight">
                        {isSuccess ? 'Order Cancelled' : 'Verify Cancellation'}
                    </DialogTitle>
                    <DialogDescription className="text-gray-500 font-medium text-base mt-2">
                        {isSuccess 
                            ? 'Your order has been successfully cancelled. A refund will be processed shortly.'
                            : 'Please enter the 6-digit verification code sent to your email address to confirm your cancellation.'
                        }
                    </DialogDescription>
                </DialogHeader>

                {!isSuccess && (
                   <div className="py-6 flex flex-col items-center ">
                        <InputOTP 
                            maxLength={6} 
                            value={otp} 
                            onChange={setOtp}
                            disabled={isPending}
                        >
                            <InputOTPGroup className="gap-2 md:gap-3">
                                {[0, 1, 2, 3, 4, 5].map((idx) => (
                                    <InputOTPSlot 
                                        key={idx} 
                                        index={idx} 
                                        className="w-12 h-14 md:w-14 md:h-16 text-xl font-black rounded-2xl border-gray-100 bg-gray-50/50 focus:bg-white transition-all" 
                                    />
                                ))}
                            </InputOTPGroup>
                        </InputOTP>

                        {errorMsg && (
                            <Paragraph className="text-destructive text-sm font-bold mt-4 animate-shake">
                                {errorMsg}
                            </Paragraph>
                        )}
                   </div>
                )}

                <DialogFooter className="sm:flex-col gap-3 mt-4">
                    {!isSuccess && (
                        <Button
                            fullWidth
                            onClick={handleConfirm}
                            disabled={otp.length !== 6 || isPending}
                        >
                            {isPending ? (
                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            ) : null}
                            Confirm Cancellation
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        fullWidth
                        onClick={onClose}
                        className="text-gray-500 font-bold"
                        disabled={isPending && !isSuccess}
                    >
                        {isSuccess ? 'Close' : 'Cancel'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
